import { Component, Input } from '@angular/core';
import { MemberContributionInvoiceItem } from 'src/app/core/models/member-contribution-invoice-item';
import { CompanyContributionInvoiceItem } from 'src/app/core/models/company-contribution-invoice-item';

/**
 * Shared invoice-items table (ported from Atenza Task B.4.4).
 *
 * Replaces five copy-pasted item-table blocks across the funder +
 * company portals (member invoice show, company invoice show,
 * interim invoice show — for both portals). Renders the row pattern
 * the Adjustments port introduced once: status notes (Suspended /
 * Terminated), adjustment-derived rows tinted yellow, signed-amount
 * colouring.
 *
 * Inputs:
 *   items     — array of either invoice item type. The component
 *               only reads fields they share, plus the optional ones
 *               (member_number, dependent details) that exist on
 *               CompanyContributionInvoiceItem.
 *   currency  — currency code passed through to customCurrency.
 *   mode      — 'member' shows the leaner member-invoice column set
 *               (Beneficiary # · Relationship · Name · Scheme · Amount
 *               · Item Scheme). 'corporate' shows the wider corporate
 *               set (Member # · Beneficiary # · First · Last · Rel ·
 *               DOB · Joined · Scheme · Amount).
 */
type InvoiceItem = MemberContributionInvoiceItem | CompanyContributionInvoiceItem;

@Component({
  selector: 'app-invoice-items-table',
  templateUrl: './invoice-items-table.component.html',
  styleUrls: ['./invoice-items-table.component.scss'],
})
export class InvoiceItemsTableComponent {
  @Input() items: Array<InvoiceItem> = [];
  @Input() currency: string = '';
  @Input() mode: 'member' | 'corporate' = 'member';

  isAdjustmentLine(item: InvoiceItem): boolean {
    return (item as any).isAdjustmentLine?.() === true;
  }

  /** Cast helper for templates that need to read corporate-only fields. */
  asCorporate(item: InvoiceItem): CompanyContributionInvoiceItem {
    return item as CompanyContributionInvoiceItem;
  }

  asMember(item: InvoiceItem): MemberContributionInvoiceItem {
    return item as MemberContributionInvoiceItem;
  }

  /** Bootstrap class for the amount cell — green for +ve adjustments,
   *  red for any negative amount, default for plain dependent items. */
  amountClass(item: InvoiceItem): string {
    if (item.contribution_amount < 0) return 'text-danger fw-bolder';
    if (item.contribution_amount > 0 && this.isAdjustmentLine(item)) return 'text-success fw-bolder';
    return '';
  }
}
